import { useEffect } from 'react';
import { isOpenMatterStatus } from '@/lib/matterStatus';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Scales,
  CalendarBlank,
  Briefcase,
  ArrowRight,
  CircleNotch,
  CheckCircle,
  XCircle,
  Clock,
  MinusCircle,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { RowActions } from '@/components/shared/RowActions';
import { fetchMyBarristers } from '@/lib/barristerClerksApi';
import { useMattersStore } from '@/lib/mattersStore';
import { useHearingsStore } from '@/lib/hearingsStore';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

const AVAIL_CONFIG: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'default'; icon: React.ReactNode }> = {
  available: { label: 'Available', variant: 'success', icon: <CheckCircle className="h-3 w-3" weight="fill" /> },
  partial:   { label: 'Partial',   variant: 'warning', icon: <Clock className="h-3 w-3" weight="fill" /> },
  busy:      { label: 'Busy',      variant: 'danger',  icon: <XCircle className="h-3 w-3" weight="fill" /> },
  on_leave:  { label: 'On leave',  variant: 'default', icon: <MinusCircle className="h-3 w-3" weight="fill" /> },
  unavailable: { label: 'Unavailable', variant: 'danger', icon: <XCircle className="h-3 w-3" weight="fill" /> },
};

function rankLabel(memberType: string): string {
  if (memberType === 'silk') return 'KC';
  if (memberType === 'junior') return 'Junior';
  if (memberType === 'pupil') return 'Pupil';
  return memberType;
}

export default function ClerkMyBarristersPage() {
  const navigate = useNavigate();

  const { data: assignments = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['my-barristers'],
    queryFn: fetchMyBarristers,
    staleTime: 30_000,
  });

  const storeMtrs = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  const hearings = useHearingsStore((s) => s.hearings);
  const fetchHearings = useHearingsStore((s) => s.fetchHearings);

  useEffect(() => {
    fetchMatters();
    fetchHearings();
  }, [fetchMatters, fetchHearings]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="My Barristers"
        subtitle={
          isError
            ? 'Could not load your assignments'
            : `${assignments.length} barrister${assignments.length !== 1 ? 's' : ''} assigned to you`
        }
      />

      {/* A failed fetch has no assignments either, and told the clerk they had no barristers and
          should go and ask the head of chambers to fix it. */}
      {isError ? (
        <Card className="p-6">
          <QueryErrorState what="the barristers assigned to you" onRetry={() => void refetch()} />
        </Card>
      ) : assignments.length === 0 ? (
        <Card className="p-10 text-center">
          <Scales className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">You have no barristers assigned yet.</p>
          <p className="mt-1 text-xs text-muted-foreground/70">Ask your head of chambers to assign barristers to your clerk record.</p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {assignments.map((a) => {
            const b = a.barrister;
            const userId = b.userId;
            const name = `${b.user.firstName} ${b.user.lastName}${b.user.memberType === 'silk' ? ' KC' : ''}`;
            const initials = `${b.user.firstName.charAt(0)}${b.user.lastName.charAt(0)}`.toUpperCase();
            const practiceAreas = b.practiceAreas?.map((pa) => pa.practiceArea.name) ?? [];

            // Active + listed matters for this barrister
            const myMatters = storeMtrs.filter(
              (m) => m.leadBarristerUserId === userId && isOpenMatterStatus(m.status),
            );

            // Next upcoming hearing
            const myHearings = hearings
              .filter(
                (h) =>
                  (h.leadBarristerUserId === userId || h.juniorBarristerUserId === userId) &&
                  (h.status === 'scheduled' || h.status === 'confirmed') &&
                  new Date(h.date) >= today,
              )
              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            const nextHearing = myHearings[0] ?? null;

            const availCfg = AVAIL_CONFIG[b.availability] ?? { label: b.availability, variant: 'default' as const, icon: null };

            return (
              <Card key={a.id} className="flex flex-col">
                <CardContent className="flex flex-1 flex-col gap-4 p-5">
                  {/* Header */}
                  <div className="flex items-start gap-3">
                    <Avatar
                      src={b.user.avatarUrl ?? undefined}
                      fallback={initials}
                      size="lg"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-foreground leading-tight">{name}</div>
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        <Badge variant="default" className="text-[10px]">{rankLabel(b.user.memberType)}</Badge>
                        <Badge variant={availCfg.variant} className="flex items-center gap-1 text-[10px]">
                          {availCfg.icon}{availCfg.label}
                        </Badge>
                        {a.role === 'primary' && (
                          <Badge variant="success" className="text-[10px]">Primary clerk</Badge>
                        )}
                      </div>
                    </div>
                    {/* Read-only view of supported barristers (BarristerClerk scope):
                        view profile only — no edit/deactivate from this clerk surface. */}
                    <RowActions
                      onView={() => navigate(`/clerk/barristers/${b.id}`)}
                      viewLabel="View profile"
                    />
                  </div>

                  {/* Practice areas */}
                  {practiceAreas.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {practiceAreas.slice(0, 4).map((pa) => (
                        <span key={pa} className="rounded-md bg-muted/50 px-2 py-0.5 text-[10px] text-muted-foreground">
                          {pa}
                        </span>
                      ))}
                      {practiceAreas.length > 4 && (
                        <span className="rounded-md bg-muted/50 px-2 py-0.5 text-[10px] text-muted-foreground">
                          +{practiceAreas.length - 4} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Stats row */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-0.5 rounded-lg border border-border bg-muted/20 px-3 py-2">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Briefcase className="h-3.5 w-3.5" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider">Active matters</span>
                      </div>
                      <div className="text-lg font-bold text-foreground">{myMatters.length}</div>
                    </div>
                    <div className="flex flex-col gap-0.5 rounded-lg border border-border bg-muted/20 px-3 py-2">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <CalendarBlank className="h-3.5 w-3.5" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider">Next hearing</span>
                      </div>
                      {nextHearing ? (
                        <div className="text-sm font-semibold text-foreground leading-tight">
                          {new Date(nextHearing.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          <span className="block text-[10px] font-normal text-muted-foreground truncate">{nextHearing.matterName}</span>
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground/60">None upcoming</div>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="mt-auto flex flex-wrap gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      leftIcon={<Briefcase className="h-3.5 w-3.5" />}
                      onClick={() => navigate(`/clerk/matters?barrister=${userId}`)}
                    >
                      Matters
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      leftIcon={<CalendarBlank className="h-3.5 w-3.5" />}
                      onClick={() => navigate(`/clerk/hearings`)}
                    >
                      Hearings
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      rightIcon={<ArrowRight className="h-3.5 w-3.5" />}
                      onClick={() => navigate(`/clerk/barristers/${b.id}`)}
                    >
                      Profile
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
